import React from "react";
import {Shield, ArrowLeft} from 'lucide-react';

interface PrivacyPolicyProps {
  onBack: () => void;
}

function PrivacyPolicy({ onBack }: PrivacyPolicyProps) {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-blue-900 text-white py-8">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <button
            onClick={onBack}
            className="flex items-center gap-2 text-blue-200 hover:text-white mb-4 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </button>
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8" />
            <h1 className="text-3xl md:text-4xl font-bold">Privacy Policy</h1>
          </div>
          <p className="text-blue-200 mt-2">Last updated: January 2024</p>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-xl shadow-lg p-8 space-y-8">
          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Introduction</h2>
            <p className="text-gray-700 leading-relaxed">
              Welcome to KVAGenerator.com. We respect your privacy and are committed to protecting your personal data. 
              This privacy policy explains how we collect, use, and safeguard your information when you visit our website.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Information We Collect</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We may collect and process the following types of information:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Usage data (pages visited, time spent, browser type)</li>
              <li>Device information (IP address, device type, operating system)</li>
              <li>Cookies and similar tracking technologies</li>
              <li>Any information you voluntarily provide through contact forms or comments</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Google AdSense and Advertising</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              We use Google AdSense to display advertisements on our website. Google AdSense uses cookies and web beacons 
              to serve ads based on your prior visits to our website or other websites on the Internet.
            </p>
            <div className="bg-blue-50 border-l-4 border-blue-900 p-4 my-4">
              <h3 className="font-semibold text-gray-900 mb-2">Third-Party Advertising</h3>
              <p className="text-gray-700 text-sm leading-relaxed">
                Google, as a third-party vendor, uses cookies to serve ads on our site. Google's use of the DART cookie 
                enables it to serve ads to our users based on their visit to our site and other sites on the Internet. 
                Users may opt out of the use of the DART cookie by visiting the Google Ad and Content Network privacy policy 
                at <a href="https://policies.google.com/technologies/ads" className="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">
                  https://policies.google.com/technologies/ads
                </a>
              </p>
            </div>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Cookies</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Our website uses cookies to enhance your browsing experience and to help us understand how visitors use our site. 
              Cookies are small text files stored on your device.
            </p>
            <h3 className="font-semibold text-gray-900 mb-2">Types of cookies we use:</h3>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li><strong>Essential cookies:</strong> Necessary for the website to function properly</li>
              <li><strong>Analytics cookies:</strong> Help us understand how visitors interact with our website</li>
              <li><strong>Advertising cookies:</strong> Used to deliver relevant advertisements</li>
            </ul>
            <p className="text-gray-700 leading-relaxed mt-3">
              You can control cookies through your browser settings. However, disabling cookies may affect the functionality 
              of our website and your ability to access certain features.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">How We Use Your Information</h2>
            <p className="text-gray-700 leading-relaxed mb-3">We use the collected information for:</p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Providing and maintaining our website</li>
              <li>Improving user experience and website functionality</li>
              <li>Analyzing website traffic and usage patterns</li>
              <li>Displaying relevant advertisements</li>
              <li>Complying with legal obligations</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Third-Party Links</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website may contain links to third-party websites. We are not responsible for the privacy practices 
              or content of these external sites. We encourage you to review the privacy policies of any third-party 
              sites you visit.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Data Security</h2>
            <p className="text-gray-700 leading-relaxed">
              We implement appropriate technical and organizational measures to protect your personal data against 
              unauthorized access, alteration, disclosure, or destruction. However, no method of transmission over 
              the Internet is 100% secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Your Rights</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Depending on your location, you may have the following rights regarding your personal data:
            </p>
            <ul className="list-disc list-inside space-y-2 text-gray-700 ml-4">
              <li>Right to access your personal data</li>
              <li>Right to rectification of inaccurate data</li>
              <li>Right to erasure ("right to be forgotten")</li>
              <li>Right to restrict processing</li>
              <li>Right to data portability</li>
              <li>Right to object to processing</li>
              <li>Right to withdraw consent</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Children's Privacy</h2>
            <p className="text-gray-700 leading-relaxed">
              Our website is not intended for children under 13 years of age. We do not knowingly collect personal 
              information from children under 13. If you believe we have collected information from a child under 13, 
              please contact us immediately.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">GDPR Compliance (European Users)</h2>
            <p className="text-gray-700 leading-relaxed">
              If you are located in the European Economic Area (EEA), you have certain data protection rights under 
              the General Data Protection Regulation (GDPR). We process your personal data lawfully based on consent, 
              legitimate interests, or legal obligations.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">CCPA Compliance (California Users)</h2>
            <p className="text-gray-700 leading-relaxed">
              If you are a California resident, you have specific rights under the California Consumer Privacy Act (CCPA), 
              including the right to know what personal information is collected, the right to delete personal information, 
              and the right to opt-out of the sale of personal information.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Changes to This Privacy Policy</h2>
            <p className="text-gray-700 leading-relaxed">
              We may update our Privacy Policy from time to time. We will notify you of any changes by posting the new 
              Privacy Policy on this page and updating the "Last updated" date at the top of this policy.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Contact Us</h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              If you have any questions about this Privacy Policy or our data practices, please contact us:
            </p>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-gray-700">
                <strong>Website:</strong> KVAGenerator.com<br />
                <strong>Email:</strong> privacy@kvagenerator.com
              </p>
            </div>
          </section>

          <div className="mt-8 pt-8 border-t border-gray-200">
            <button
              onClick={onBack}
              className="flex items-center gap-2 text-blue-900 hover:text-orange-500 font-semibold transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Return to Homepage
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
